import React from "react";
import { colors } from "app/theme";
import { styled } from "@mui/material/styles";
import Tooltip, { tooltipClasses, TooltipProps } from "@mui/material/Tooltip";
import { get } from "lodash";

type Placement =
  | "top"
  | "bottom"
  | "left"
  | "right"
  | "bottom-end"
  | "bottom-start"
  | "top-end"
  | "top-start"
  | undefined;

interface InfoTooltipProps {
  arrow?: boolean;
  mode: "light" | "dark" | "blue" | "green";
  children: React.ReactElement;
  info?:
    | ((opts: {
        tooltipColors: {
          text: string;
          background: string;
          border: string;
        };
        onClose?: () => void;
      }) => string | React.ReactNode)
    | string
    | React.ReactNode;
  placement?: Placement;
  width?: string;
  open?: boolean;
  offsetX?: number;
  offsetY?: number;
  onClose?: () => void;
}

export const InfoTooltip: React.FC<InfoTooltipProps> = (
  props: InfoTooltipProps
) => {
  const placement = props.placement ?? "bottom";

  const tooltipColors = {
    light: {
      text: colors.text.title,
      background: colors.secondary.lightGrey,
      border: colors.secondary.lightGrayText,
    },
    dark: {
      text: colors.primary.white,
      background: colors.text.body,
      border: colors.secondary.lightGrayText,
    },
    blue: {
      text: colors.primary.white,
      background: colors.shades.blue[400],
      border: colors.secondary.lightGrayText,
    },
    green: {
      text: colors.primary.white,
      background: colors.shades.green[400],
      border: colors.secondary.lightGrayText,
    },
  }[props.mode];

  const tooltipStyles = {
    "bottom-start": {
      left: "-13px !important",
    },
    "top-start": {
      left: "-13px !important",
    },
    "bottom-end": {
      right: "-13px !important",
    },
    "top-end": {
      right: "-13px !important",
    },
  };
  const bottomStyle = {
    marginTop: "-13px !important",
    width: "21px !important",
    height: "13px !important",
    borderBottom: `0.5px solid ${tooltipColors.border}`,
  };
  const topStyle = {
    marginBottom: "-13px !important",
    width: "21px !important",
    height: "13px !important",
    borderTop: `0.5px solid ${tooltipColors.border}`,
  };
  const arrowStyles = {
    "top-end": {
      ...topStyle,
      left: "-10px !important",
    },
    "bottom-end": {
      ...bottomStyle,
      left: "-6px !important",
    },
    "top-start": {
      ...topStyle,
      left: "5px !important",
    },
    "bottom-start": {
      ...bottomStyle,
      left: "10px !important",
    },
    left: {
      marginRight: "-13px !important",
      top: "2px !important",
      width: "13px !important",
      height: "21px !important",
      borderLeft: `0.5px solid ${tooltipColors.border}`,
    },
    right: {
      marginLeft: "-13px !important",
      top: "-3px !important",
      width: "13px !important",
      height: "21px !important",
      borderRight: `0.5px solid ${tooltipColors.border}`,
    },
    top: {
      ...topStyle,
      left: "-3px !important",
    },
    bottom: {
      ...bottomStyle,
      left: "1px !important",
    },
  };

  const CustomTooltip = React.useMemo(() => {
    return styled(({ className, ...tprops }: TooltipProps) => (
      <Tooltip {...tprops} classes={{ popper: className }} />
    ))(() => ({
      [`& .${tooltipClasses.tooltip}`]: {
        width: props.width ?? "231px",
        maxWidth: props.width ?? "300px",
        padding: "10px",
        fontSize: "12px",
        borderRadius: "4px",
        boxShadow: colors.shadows.tooltip,
        border: `0.5px solid ${tooltipColors.border}`,
        color: tooltipColors.text,
        background: tooltipColors.background,
        ...get(tooltipStyles, placement, {}),
      },
      [`& .${tooltipClasses.arrow}`]: {
        borderColor: tooltipColors.border,
        color: tooltipColors.background,
        "&::before": {
          borderRadius: "2px",
        },
        ...get(arrowStyles, placement, {}),
      },
    }));
  }, [props.mode, props.placement]);

  return (
    <CustomTooltip
      open={props.open}
      arrow={props.arrow}
      title={
        typeof props.info === "function"
          ? props.info({ tooltipColors, onClose: props.onClose })
          : props.info ?? ""
      }
      placement={placement}
      slotProps={{
        popper: {
          modifiers: [
            {
              name: "offset",
              options: {
                offset: [props.offsetX ?? 0, props.offsetY ?? 0],
              },
            },
          ],
        },
      }}
      onClose={props.onClose}
    >
      {props.children}
    </CustomTooltip>
  );
};
